import { useEffect, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Platform,
  Pressable,
  Image,
  ActivityIndicator,
} from 'react-native';
import { useNavigate } from 'react-router-dom';
import { ChevronRight, UserCheck, Users } from '../../lib/icons';
import { supabase } from '../../lib/supabase';
import { shadows } from '../../lib/styles';

interface ManagerLite {
  full_name: string | null;
  profile_photo_url: string | null;
}

interface AgentLite {
  full_name: string;
  brokerage: string | null;
  photo_url: string | null;
}

// Compact "Your Team" card for the homeowner dashboard. Fetches the
// manager (via the home-manager conversation for the active home) and the
// referring real estate agent, then nudges users to the full /team page.
const YourTeam = ({ activeHomeId }: { activeHomeId: string | null }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [manager, setManager] = useState<ManagerLite | null>(null);
  const [agent, setAgent] = useState<AgentLite | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const {
          data: { user },
        } = await supabase.auth.getUser();
        if (!user) return;

        const { data: profileRow } = await supabase
          .from('profiles')
          .select(
            `agent:real_estate_agents!profiles_referring_agent_id_fkey (
               full_name, brokerage, photo_url
             )`
          )
          .eq('id', user.id)
          .single();
        if (!cancelled) {
          setAgent(((profileRow as any)?.agent as AgentLite) || null);
        }

        if (activeHomeId) {
          const { data: convo } = await supabase
            .from('conversations')
            .select(
              `home_manager:profiles!conversations_home_manager_id_fkey (
                 full_name, profile_photo_url
               )`
            )
            .eq('homeowner_id', user.id)
            .eq('type', 'home_manager')
            .eq('home_id', activeHomeId)
            .maybeSingle();
          if (!cancelled) {
            setManager(((convo as any)?.home_manager as ManagerLite) || null);
          }
        }
      } catch (err) {
        console.error('Error loading dashboard team widget:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeHomeId]);

  // Hide entirely when there's nothing useful to show. The home-manager
  // banner at the top of the dashboard already covers the manager-only case.
  if (!loading && !agent) {
    return null;
  }

  return (
    <Pressable
      style={({ pressed }) => [styles.card, pressed && styles.cardPressed]}
      onPress={() => navigate('/team')}
    >
      <View style={styles.header}>
        <View style={styles.titleRow}>
          <Users size={16} color="#0f172a" />
          <Text style={styles.title}>Your Team</Text>
        </View>
        <ChevronRight size={18} color="#9ca3af" />
      </View>

      {loading ? (
        <ActivityIndicator size="small" color="#9ca3af" />
      ) : (
        <View style={styles.membersRow}>
          {manager ? (
            <Member
              label="Home Manager"
              name={manager.full_name || 'Assigned soon'}
              photoUrl={manager.profile_photo_url}
              accent="#1d4ed8"
              accentBg="#dbeafe"
            />
          ) : null}
          {agent ? (
            <Member
              label="Real Estate Agent"
              name={agent.full_name}
              subtitle={agent.brokerage || undefined}
              photoUrl={agent.photo_url}
              accent="#0f766e"
              accentBg="#ccfbf1"
              icon={<UserCheck size={12} color="#0f766e" />}
            />
          ) : null}
        </View>
      )}
    </Pressable>
  );
};

const Member = ({
  label,
  name,
  subtitle,
  photoUrl,
  accent,
  accentBg,
  icon,
}: {
  label: string;
  name: string;
  subtitle?: string;
  photoUrl?: string | null;
  accent: string;
  accentBg: string;
  icon?: React.ReactNode;
}) => {
  const initial = name.charAt(0).toUpperCase();
  return (
    <View style={styles.member}>
      {photoUrl ? (
        <Image source={{ uri: photoUrl }} style={styles.avatar} />
      ) : (
        <View style={[styles.avatar, styles.avatarFallback, { backgroundColor: accentBg }]}>
          <Text style={[styles.avatarFallbackText, { color: accent }]}>{initial}</Text>
        </View>
      )}
      <View style={styles.memberInfo}>
        <View style={styles.memberLabelRow}>
          {icon}
          <Text style={[styles.memberLabel, { color: accent }]}>{label}</Text>
        </View>
        <Text style={styles.memberName} numberOfLines={1}>
          {name}
        </Text>
        {subtitle ? (
          <Text style={styles.memberSubtitle} numberOfLines={1}>
            {subtitle}
          </Text>
        ) : null}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  card: {
    backgroundColor: '#ffffff',
    borderRadius: 16,
    borderWidth: 1,
    borderColor: '#f1f5f9',
    padding: 16,
    gap: 12,
    ...(Platform.OS === 'web' && shadows.sm),
  },
  cardPressed: {
    opacity: 0.85,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  titleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  title: {
    fontSize: 15,
    fontWeight: '700',
    color: '#0f172a',
  },
  membersRow: {
    flexDirection: 'row',
    gap: 12,
    flexWrap: 'wrap',
  },
  member: {
    flex: 1,
    minWidth: 200,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  avatar: {
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: '#e5e7eb',
  },
  avatarFallback: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  avatarFallbackText: {
    fontSize: 16,
    fontWeight: '700',
  },
  memberInfo: {
    flex: 1,
    gap: 2,
  },
  memberLabelRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  memberLabel: {
    fontSize: 10,
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: 0.4,
  },
  memberName: {
    fontSize: 14,
    fontWeight: '600',
    color: '#0f172a',
  },
  memberSubtitle: {
    fontSize: 12,
    color: '#6b7280',
  },
});

export default YourTeam;
